import React, { useState, useMemo } from 'react';
import styled from 'styled-components';

import { LayoutColors } from '@src/core/theme';
import { CartCouponTypeEnum, Order } from '@src/core/buyerApiClient';
import { device } from '@src/common/breakpoints';
import { FiDownloadCloud } from 'react-icons/fi';
import OrderStatusDisplay from '@src/components/ui/OrderStatusDisplay';
import { downloadBlob, formatCents, formatDateTime } from '@src/common';
import { BodyLargeBold, BodyRegular, BodyRegularSemiBold, BodySmall, Heading6 } from '@src/fonts/typography/Typography';
import { useAppSelector, useTranslate } from '@src/core/hooks';

import { baseButton } from '../shared';
import DotFalling from '@src/components/reusable/spinner/DotFalling';
import { getOrderVouchers } from '@src/common/vouchers';
import { orderVoucherInfoSelector } from '@src/features/order';
import { Tooltip } from '@src/components/reusable/Tooltip';

type Props = {
  order: Order;
};

const HeaderContent = ({ order }: Props) => {
  const { i18n, i18nElement } = useTranslate();

  const [loadingVouchersPdf, setLoadingVouchersPdf] = useState<boolean>(false);
  const { hasMultipleVouchers, hasPendingVouchers } = useAppSelector(orderVoucherInfoSelector(order.id));

  const handleOpenVouchers = async () => {
    setLoadingVouchersPdf(true);
    const pdfBlob = await getOrderVouchers(order.id);

    if (pdfBlob) {
      downloadBlob(pdfBlob, `vouchers-${order.id}.pdf`);
    }

    setLoadingVouchersPdf(false);
  };

  const saleCoupon = useMemo(() => {
    return order.sales.find((sale) => sale.coupon)?.coupon;
  }, [order]);

  return (
    <Container>
      <HeaderMainInfo>
        <AccountNumberWrapper>
          <h3>
            {i18nElement('account_sellNumber', {
              orderCode: order.code,
            })}
          </h3>

          {hasPendingVouchers ? (
            <VoucherStatus>{i18n('account_sale_voucher_pending')}</VoucherStatus>
          ) : (
            <OrderStatusDisplay status={order.status} />
          )}
        </AccountNumberWrapper>

        {hasMultipleVouchers ? (
          <Tooltip
            arrow
            placement='top'
            showContent={hasPendingVouchers}
            label={i18n('account_sale_download_all_vouchers_tooltip')}
          >
            <Button onClick={() => handleOpenVouchers()} disabled={hasPendingVouchers || loadingVouchersPdf}>
              {loadingVouchersPdf ? (
                <DotFalling />
              ) : (
                <>
                  <FiDownloadCloud color={LayoutColors.whiteLight} size={22} />
                  {i18n('account_sale_download_all')}
                </>
              )}
            </Button>
          </Tooltip>
        ) : null}
      </HeaderMainInfo>

      {hasPendingVouchers ? (
        <PendingVoucherInfo>{i18nElement('account_sale_pending_voucher_info')}</PendingVoucherInfo>
      ) : null}

      <Details>
        <div>
          <span>{i18n('account_sale_date')}:</span>
          <span>{formatDateTime(order.createdAt)}</span>
        </div>
        {saleCoupon ? (
          <div>
            <span>{i18n('account_sale_discount')}:</span>
            <p>
              {saleCoupon?.type === CartCouponTypeEnum.Percentage ? (
                <span>{`(-${saleCoupon.formattedDiscount})`}</span>
              ) : null}

              <span>{formatCents(saleCoupon.fullAmountCents - saleCoupon.finalAmountCents)}</span>
            </p>
          </div>
        ) : null}

        <div>
          <span>{i18n('account_sale_total_value')}:</span>
          <strong>{formatCents(order.finalAmountCents)}</strong>
        </div>
      </Details>
    </Container>
  );
};

export default HeaderContent;

const Container = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const HeaderMainInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${device.smallTablet} {
    flex-direction: row;
    align-items: baseline;
    justify-content: space-between;
  }
`;

const AccountNumberWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;

  h3 {
    ${BodyRegularSemiBold};
    color: ${LayoutColors.blackMain};
  }

  ${device.smallTablet} {
    gap: 22px;

    h3 {
      ${Heading6};
    }
  }
`;
const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;

  padding-bottom: 16px;
  border-bottom: 1px solid ${LayoutColors.grayMain};

  div {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;

    span {
      ${BodySmall};
      color: ${LayoutColors.blackMain};
    }

    p {
      display: flex;
      flex-direction: row;
      gap: 8px;
      align-items: center;
    }

    strong {
      ${BodyRegularSemiBold};
      color: ${LayoutColors.blackMain};
    }
  }

  ${device.smallTablet} {
    border-bottom: none;

    div {
      span {
        ${BodyRegular};
      }

      strong {
        ${BodyLargeBold};
      }
    }
  }
`;

const Button = styled.button`
  ${baseButton};
  text-transform: uppercase;

  color: ${LayoutColors.whiteLight};
  background: ${LayoutColors.justBlue};

  ${device.smallTablet} {
    min-width: 190px;
    height: 44px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const VoucherStatus = styled.p`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  background: ${LayoutColors.warningDark};
  border-radius: 8px;

  ${BodySmall};
  color: ${LayoutColors.whiteLight};
  padding: 8px;
`;

const PendingVoucherInfo = styled.p`
  width: 100%;
  max-width: 504px;

  ${BodyRegular}
  color: ${LayoutColors.blackMain};
`;
